import { yupResolver } from "@hookform/resolvers/yup";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { WalletItemType } from "@/components/serviceFlow/wallet/items/walletItems";
import { DiscountType } from "@/types";
import { numberIsGreaterThanZero, numberIsPercentValue } from "@/utils";

export enum DiscountFormStep {
  FORM = "form",
  SELECT_SERVICES = "selectServices",
  SELECT_PRODUCTS = "selectProducts",
}

export enum DiscountTypeValues {
  FIXED_VALUE = "fixed_value",
  PERCENT = "percent",
}

const schema = yup.object().shape({
  type: yup.string(),
  name: yup.string().required("Please provide discount name"),
  discountType: yup.string().required("Please select discount type"),
  value: yup
    .string()
    .when("discountType", (discountType: string, schema: yup.StringSchema) => {
      if (discountType === DiscountTypeValues.FIXED_VALUE) {
        return schema
          .required("Please provide valid discount amount")
          .test("value", numberIsGreaterThanZero);
      }
      if (discountType === DiscountTypeValues.PERCENT) {
        return schema
          .required("Please provide valid discount amount")
          .test(
            "value",
            "Please provide valid discount amount",
            numberIsPercentValue
          );
      }

      return schema.notRequired();
    }),
  quantityEnabled: yup.boolean(),
  services: yup.string().when("type", (type: string) => {
    if (type === DiscountType.SERVICE) {
      return yup.array().min(1, "Please select at least one service");
    }
    return yup.array().notRequired();
  }),

  products: yup.string().when("type", (type: string) => {
    if (
      type === DiscountType.RETAIL_PRODUCT ||
      type === DiscountType.SERVICE_PRODUCT
    ) {
      return yup.array().min(1, "Please select at least one product");
    }
    return yup.array().notRequired();
  }),

  quantityLimit: yup
    .string()
    .when("quantityEnabled", (quantityEnabled: boolean) => {
      if (quantityEnabled) {
        return yup
          .string()
          .required("Please provide valid quantity limit")
          .test(
            "quantityLimit",
            "Please provide valid quantity limit",
            numberIsGreaterThanZero
          );
      }
      return yup.string().notRequired();
    }),
  saveDiscount: yup.boolean(),
});

export type DiscountFormInput = {
  type?: string;
  name: string;
  discountType: DiscountTypeValues;
  fixed_value: string;
  percent: string;
  value: string;
  saveDiscount?: boolean;
  quantityEnabled?: boolean;
  quantityLimit?: string;
  services?: string[];
  products?: string[];
};

type UseDiscountsCommon = {
  discountType: DiscountType;
  retailProducts?: {
    id: string;
    name: string;
    price: string;
  }[];
  serviceProducts?: {
    id: string;
    name: string;
    price: string;
  }[];
};

const productsHaveSamePrice = (
  productArray: { id: string; price: string }[],
  selectedProducts: string[]
) => {
  if (!productArray) return true;
  if (productArray.length === 0) return true;

  return selectedProducts.every((product, _index, arr) => {
    const firstProductPrice = productArray.find((p) => p.id === arr[0])?.price;

    const productPrice = productArray.find((p) => p.id === product)?.price;

    return firstProductPrice === productPrice;
  });
};

export default function useDiscountsCommon({
  discountType,
  retailProducts,
  serviceProducts,
}: UseDiscountsCommon) {
  const discountFormMethods = useForm<DiscountFormInput>({
    resolver: yupResolver(schema),
    defaultValues: {
      type: discountType,
      products: [],
      services: [],
    },
  });

  const [step, setStep] = useState(DiscountFormStep.FORM);
  const [services, setServices] = useState<string[]>([]);
  const [products, setProducts] = useState<string[]>([]);

  const handleItemsSave = (tempItems: string[], item: WalletItemType) => {
    const itemsSetter = item === "service" ? setServices : setProducts;
    itemsSetter(tempItems);
    setStep(DiscountFormStep.FORM);
  };

  const handleDeleteItem = (itemId: string, item: WalletItemType) => {
    const itemsSetter = item === "service" ? setServices : setProducts;
    itemsSetter((prev) => prev.filter((id) => id !== itemId));
  };

  const productsByDiscountType = useMemo(() => {
    if (discountType === DiscountType.RETAIL_PRODUCT) {
      return retailProducts;
    }
    if (discountType === DiscountType.SERVICE_PRODUCT) {
      return serviceProducts;
    }

    return [];
  }, [discountType, retailProducts, serviceProducts]);

  const hasSamePriceProducts = useMemo(
    () => productsHaveSamePrice(productsByDiscountType, products),
    [products, productsByDiscountType]
  );

  return {
    step,
    setStep,
    services,
    setServices,
    products,
    setProducts,
    handleItemsSave,
    handleDeleteItem,
    productsByDiscountType,
    hasSamePriceProducts,
    discountFormMethods,
  };
}
